import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function ImageCarousel({ images, height = "400px", className = "" }) {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => setCurrent(c => (c + 1) % images.length), [images.length]);
  const prev = useCallback(() => setCurrent(c => (c - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  if (!images || images.length === 0) return null;

  return (
    <div
      className={`relative rounded-2xl overflow-hidden group ${className}`}
      style={{ height }}
      data-testid="image-carousel"
    >
      {images.map((img, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-700 ease-in-out"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          <img
            src={img.url}
            alt={img.caption || ""}
            className="w-full h-full object-cover object-center"
            loading={i === current ? "eager" : "lazy"}
            decoding="async"
          />
        </div>
      ))}

      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            data-testid="carousel-prev-btn"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{ background: "rgba(255,255,255,0.8)", backdropFilter: "blur(8px)" }}
          >
            <ChevronLeft size={16} style={{ color: "#0D3A4F" }} />
          </button>
          <button
            onClick={next}
            data-testid="carousel-next-btn"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{ background: "rgba(255,255,255,0.8)", backdropFilter: "blur(8px)" }}
          >
            <ChevronRight size={16} style={{ color: "#0D3A4F" }} />
          </button>

          {images.length <= 10 ? (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className="transition-all duration-300 rounded-full"
                  style={{
                    width: i === current ? "20px" : "8px",
                    height: "8px",
                    background: i === current ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.5)"
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="absolute bottom-4 right-4 z-10">
              <span
                className="text-xs px-3 py-1.5 rounded-full"
                style={{ background: "rgba(0,0,0,0.5)", color: "rgba(255,255,255,0.9)", backdropFilter: "blur(4px)" }}
              >
                {current + 1} / {images.length}
              </span>
            </div>
          )}

          {images[current]?.caption && (
            <div className="absolute bottom-12 left-4 z-10">
              <span
                className="text-xs px-3 py-1.5 rounded-full"
                style={{ background: "rgba(0,0,0,0.5)", color: "rgba(255,255,255,0.9)", backdropFilter: "blur(4px)" }}
              >
                {images[current].caption}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
